import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { validateTokenComprehensive, validateTokenSilent } from '../Utils/authUtils';
import { logout } from '../Redux/Action/LoginAction/loginAction';

/**
 * Custom hook for token validation that runs on every page load/reload
 * and automatically logs out users if the token cookie is missing
 */
export const useTokenValidation = () => {
  const dispatch = useDispatch();
  const isLoggingOut = useRef(false);

  const validateToken = useCallback(() => {
    // Prevent multiple logout attempts
    if (isLoggingOut.current) {
      return false;
    }

    const validation = validateTokenComprehensive();
    
    if (validation.shouldLogout) {
      console.log('Token validation failed:', validation.reason);
      
      // Set flag to prevent multiple logout attempts
      isLoggingOut.current = true;
      
      // Dispatch logout action to clear all authentication state
      dispatch(logout());
      
      // Force redirect to login page
      window.location.href = '/login';
      return false;
    }
    
    return validation.isValid;
  }, [dispatch]);

  const validateTokenSilently = useCallback(() => {
    // Use silent validation for background checks
    const validation = validateTokenSilent();
    
    if (validation.shouldLogout && !isLoggingOut.current) {
      console.log('Silent token validation failed, logging out user');
      isLoggingOut.current = true;
      dispatch(logout());
      window.location.href = '/login';
      return false;
    }
    
    return validation.isValid;
  }, [dispatch]);

  useEffect(() => {
    // Run validation immediately on mount
    validateToken();

    // Run validation when the window gains focus (handles tab switching)
    const handleFocus = () => {
      validateTokenSilently();
    };

    // Run validation when the page becomes visible (handles page refresh)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        validateTokenSilently();
      }
    };

    // Run validation on route changes (for SPA navigation)
    const handlePopState = () => {
      validateTokenSilently();
    };

    // Run validation on storage changes (handles localStorage changes from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'accessToken' || e.key === null) {
        validateTokenSilently();
      }
    };

    // Run validation periodically (every 30 seconds) for security
    const intervalId = setInterval(() => {
      validateTokenSilently();
    }, 30000);

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [validateToken, validateTokenSilently]);

  // Reset logout flag when component unmounts
  useEffect(() => {
    return () => {
      isLoggingOut.current = false;
    };
  }, []);

  return {
    validateToken,
    validateTokenSilently,
    isTokenValid: validateTokenComprehensive().isValid
  };
};
